// apps/client/src/ui-kit/OnboardingSVGs.tsx
import { motion } from 'framer-motion';

// --- Base Perfect Coin SVG component for reuse ---
export const PerfectCoin = ({ className = '', size }: { className?: string, size?: number }) => (
    <svg
        width={size || '100%'}
        height={size || '100%'}
        viewBox="0 0 512 512"
        xmlns="http://www.w3.org/2000/svg"
        className={`shrink-0 w-full h-full ${className}`}
        style={{ filter: 'drop-shadow(0px 5px 8px rgba(0,0,0,0.3))' }}
        preserveAspectRatio="xMidYMid meet"
    >
        <circle cx="256" cy="256" fill="#ffb703" r="256" />
        <circle cx="256" cy="256" fill="#ffcc29" r="183.75" />
        <g fill="none" stroke-miterlimit="10">
            <circle cx="256" cy="256" r="183.75" stroke="#f99300" strokeWidth="32.32" />
            <path d="m341.31 468.54a227.51 227.51 0 0 0 142.15-210.92" stroke="#ffcc29" strokeLinecap="round" strokeWidth="19.39" />
            <path d="m142.26 59a226.34 226.34 0 0 1 113.74-30.46" stroke="#ffcc29" strokeLinecap="round" strokeWidth="19.39" />
            <path d="m64.77 132.76a228.18 228.18 0 0 1 39.64-46.37" stroke="#ffcc29" strokeLinecap="round" strokeWidth="19.39" />
            <path d="m56.22 364.84a226.58 226.58 0 0 1 -27.68-108.84" stroke="#ffcc29" strokeLinecap="round" strokeWidth="19.39" />
        </g>
        <path d="m303.53 237.67-44.85-64.91a3.25 3.25 0 0 0 -5.36 0l-44.85 64.91a3.26 3.26 0 0 1 -4 1.14l-63.64-28.27a3.27 3.27 0 0 0 -4.46 3.89l36.9 126.36a3.26 3.26 0 0 0 3.13 2.35h159.21a3.26 3.26 0 0 0 3.13-2.35l36.9-126.36a3.27 3.27 0 0 0 -4.46-3.89l-63.64 28.27a3.29 3.29 0 0 1 -4.01-1.14z" fill="#f99300" />

        {/* Glow & 3D Lighting Overlay added for Onboarding */}
        <circle cx="200" cy="180" r="80" fill="url(#coinGlow)" opacity="0.6" />
        <defs>
            <radialGradient id="coinGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="white" stopOpacity="0.8" />
                <stop offset="100%" stopColor="white" stopOpacity="0" />
            </radialGradient>
        </defs>
    </svg>
);

// --- Slide 1: The Hook (Confetti & Balls) ---
export const HookAnimationSVG = () => {
    return (
        <div className="relative w-64 h-64 flex items-center justify-center">
            {/* Confetti Background */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
                {[...Array(12)].map((_, i) => (
                    <motion.rect
                        key={`confetti-${i}`}
                        x={100} y={100}
                        width={6} height={12}
                        fill={['#FFC107', '#E91E63', '#00E676', '#00B0FF'][i % 4]}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{
                            opacity: [0, 1, 0],
                            x: Math.cos(i * (Math.PI / 6)) * (80 + Math.random() * 40),
                            y: Math.sin(i * (Math.PI / 6)) * (80 + Math.random() * 40),
                            rotate: Math.random() * 360
                        }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: Math.random() * 1 }}
                    />
                ))}
            </svg>

            {/* Floating Bingo Balls */}
            <motion.div
                className="absolute top-4 left-4 w-16 h-16 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-[0_10px_20px_rgba(220,38,38,0.5)] flex items-center justify-center border-2 border-white/20"
                animate={{ y: [0, -15, 0], rotate: [0, 10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center font-black text-red-600 shadow-inner">7</div>
            </motion.div>

            <motion.div
                className="absolute bottom-8 right-4 w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 shadow-[0_10px_20px_rgba(37,99,235,0.5)] flex items-center justify-center border-2 border-white/20"
                animate={{ y: [0, 20, 0], rotate: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            >
                <img src="/favicon.png" className="w-10 h-10 object-contain drop-shadow-md" alt="BingoTon" />
            </motion.div>

            {/* Central BingoTon Logo Graphic */}
            <motion.div
                animate={{ scale: [1, 1.05, 1], y: [0, -5, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                className="z-10 relative"
            >
                <div className="text-center bg-gradient-to-b from-white/20 to-transparent backdrop-blur-md p-6 rounded-full border border-white/20 shadow-[-10px_-10px_30px_rgba(255,255,255,0.1),10px_10px_30px_rgba(0,0,0,0.5)]">
                    <span className="text-5xl">🎉</span>
                </div>
            </motion.div>
        </div>
    );
};

// --- Slide 2: The Loop (Stack of Coins) ---
export const CoinStackSVG = () => {
    return (
        <div className="relative w-64 h-64 flex items-center justify-center">
            {/* Background Glow */}
            <div className="absolute inset-0 bg-yellow-500/20 blur-[50px] rounded-full" />

            <div className="relative w-48 h-56">
                {/* 3D Coin Stack - Order dictates z-index */}
                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ type: 'spring', damping: 12, delay: 0.1 }}
                    className="absolute bottom-0 left-4"
                >
                    <PerfectCoin size={120} />
                </motion.div>

                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: -30, opacity: 1 }}
                    transition={{ type: 'spring', damping: 12, delay: 0.3 }}
                    className="absolute bottom-0 left-6"
                >
                    <PerfectCoin size={120} />
                </motion.div>

                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: -60, opacity: 1 }}
                    transition={{ type: 'spring', damping: 12, delay: 0.5 }}
                    className="absolute bottom-0 left-10"
                >
                    <PerfectCoin size={120} />
                </motion.div>

                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: -90, opacity: 1 }}
                    transition={{ type: 'spring', damping: 12, delay: 0.7 }}
                    className="absolute bottom-0 left-12"
                >
                    <PerfectCoin size={120} />
                </motion.div>

                {/* Floating standalone coin */}
                <motion.div
                    animate={{ y: [-15, 10, -15], rotate: [-5, 5, -5] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-0 -right-4"
                >
                    <PerfectCoin size={90} />
                </motion.div>

                {/* Sparkles */}
                <motion.div
                    animate={{ opacity: [0, 1, 0], scale: [0.5, 1.2, 0.5], rotate: 180 }}
                    transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                    className="absolute top-10 left-0 text-yellow-300 text-3xl"
                >
                    ✨
                </motion.div>
                <motion.div
                    animate={{ opacity: [0, 1, 0], scale: [0.5, 1.5, 0.5], rotate: -90 }}
                    transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                    className="absolute bottom-10 -right-8 text-yellow-300 text-4xl"
                >
                    ✨
                </motion.div>
            </div>
        </div>
    );
};

// --- Slide 3: The Reward (Open Treasure Chest with TON) ---
export const TreasureChestSVG = () => {
    return (
        <div className="relative w-64 h-64 flex items-center justify-center">
            {/* Ethereal Glow */}
            <motion.div
                animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.1, 1] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute inset-0 bg-blue-500/30 blur-[60px] rounded-full"
            />

            <div className="relative z-10 w-full h-full flex flex-col items-center justify-center">
                {/* SVG Treasure Chest Base */}
                <svg width="180" height="150" viewBox="0 0 200 160" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-[0_20px_30px_rgba(0,0,0,0.6)]">
                    <defs>
                        <linearGradient id="chestWood" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#8B4513" />
                            <stop offset="100%" stopColor="#3E1F07" />
                        </linearGradient>
                        <linearGradient id="chestGold" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#FFDF00" />
                            <stop offset="50%" stopColor="#B8860B" />
                            <stop offset="100%" stopColor="#FFDF00" />
                        </linearGradient>
                        <linearGradient id="insideGlow" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#4A90E2" stopOpacity="0.8" />
                            <stop offset="100%" stopColor="#1C4B82" stopOpacity="0.1" />
                        </linearGradient>
                    </defs>

                    {/* Open Inside specific glow */}
                    <path d="M20 70 L180 70 L170 140 L30 140 Z" fill="url(#insideGlow)" />

                    {/* Floating TON Crystal emerging from chest */}
                    <g transform="translate(60, 10)">
                        <motion.g
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: [-5, -15, -5], opacity: 1 }}
                            transition={{
                                y: { duration: 2.5, repeat: Infinity, ease: "easeInOut" },
                                opacity: { duration: 1 }
                            }}
                        >
                            {/* Blue TON diamond SVG representation */}
                            <path d="M40 0 L80 30 L40 60 L0 30 Z" fill="#0098EA" />
                            <path d="M40 0 L80 30 L40 30 Z" fill="#32BAFF" />
                            <path d="M40 30 L80 30 L40 60 Z" fill="#0072B1" />
                            <path d="M0 30 L40 30 L40 60 Z" fill="#005A8C" />
                            <path d="M0 30 L40 0 L40 30 Z" fill="#189CF0" />
                        </motion.g>
                    </g>

                    {/* Front panel */}
                    <path d="M10 80 L190 80 L180 150 L20 150 Z" fill="url(#chestWood)" />
                    {/* Metal bands */}
                    <rect x="30" y="80" width="15" height="70" fill="url(#chestGold)" />
                    <rect x="155" y="80" width="15" height="70" fill="url(#chestGold)" />
                    {/* Lock */}
                    <rect x="85" y="70" width="30" height="35" rx="5" fill="url(#chestGold)" />
                    <circle cx="100" cy="90" r="5" fill="#3E1F07" />

                    {/* Open Lid (angled up and back) */}
                    <path d="M30 40 Q100 -10 170 40 L190 75 Q100 50 10 75 Z" fill="url(#chestWood)" />
                    {/* Lid Metal bands */}
                    <path d="M45 30 L55 60 L40 65 L30 35 Z" fill="url(#chestGold)" />
                    <path d="M155 30 L170 35 L160 65 L145 60 Z" fill="url(#chestGold)" />
                    {/* Lid Edge */}
                    <path d="M10 75 Q100 50 190 75 L185 85 Q100 60 15 85 Z" fill="url(#chestGold)" />
                </svg>
            </div>
        </div>
    );
};
